// Project ▸ Agents on the Web (PROTOCOL.md §8): the switch that makes this project server-readable, so agents such as
// Claude can work on it through the relay's MCP endpoint; the agents connected to it, with Disconnect; and every call
// they made, newest first. A call's arguments and result are sealed with the project key on the relay: they're opened
// here, in the browser, when the writer asks.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  agentCalls, agentsInfo, agentsProjects, openCall, revokeGrant, turnOffAgents, turnOnAgents, type AgentCall, type AgentGrant,
} from '@writing-suite/client/net/agents.ts';
import type { AgentCallDetails } from '@writing-suite/client/shared/escrow.ts';
import type { PageProps } from '../shell/ProjectShell.tsx';
import { announce, useApp } from '../shell/context.ts';
import { ConfirmDialog } from './Menu.tsx';

const WHEN = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', second: '2-digit' });
const DAY = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
const OUTCOME: Record<AgentCall['outcome'], string> = { ok: 'Done', refused: 'Refused', error: 'Failed' };

function permissions(g: AgentGrant['gates']): string {
  return ['Read', g.comment && 'Comment', g.organise && 'Organise', g.add && 'Add drafts and folders', g.trash && 'Move to the Trash'].filter(Boolean).join(', ');
}

/** Agents on the web are the owner's (W9a): a project shared with this account says so instead. */
export function AgentsPage(props: PageProps) {
  const app = useApp();
  const listed = app.projects?.find((p) => p.id === props.projectId) ?? null;
  if (listed?.shared) {
    return (
      <div className="agents-page">
        <header className="page-head">
          <h1 tabIndex={-1}>Agents on the Web</h1>
        </header>
        <p className="lede owner-only" data-owner-only="agents">
          Only {listed.shared.ownerName || 'the project’s owner'} can let agents work on this project.
        </p>
      </div>
    );
  }
  return <OwnAgentsPage {...props} />;
}

function OwnAgentsPage({ projectId }: PageProps) {
  const app = useApp();
  const api = app.account.session.api;
  const listed = app.projects?.find((p) => p.id === projectId) ?? null;
  const [on, setOn] = useState<boolean | null>(null);
  const [grants, setGrants] = useState<AgentGrant[]>([]);
  const [calls, setCalls] = useState<AgentCall[] | null>(null);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOff, setConfirmOff] = useState(false);
  const [disconnecting, setDisconnecting] = useState<AgentGrant | null>(null);
  const mcpURL = `${location.origin}/mcp`;

  const refresh = useCallback(async () => {
    if (!app.online) return;
    try {
      const [readable, info, list] = await Promise.all([agentsProjects(api), agentsInfo(api), agentCalls(api, projectId, { limit: 50 })]);
      setOn(readable.get(projectId) ?? false);
      setGrants(info.grants.filter((g) => g.projects.includes(projectId)));
      setCalls(list);
      setMore(list.length === 50);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [api, projectId, app.online]);
  useEffect(() => void refresh(), [refresh]);

  const switchOn = async () => {
    if (!listed) return setError('This project’s key isn’t open on this computer.');
    setBusy(true);
    try {
      await turnOnAgents(api, app.account.accountId, projectId, listed.key);
      announce('Agents on the web turned on.');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const switchOff = async () => {
    setBusy(true);
    try {
      await turnOffAgents(api, projectId);
      announce('Agents on the web turned off.');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const older = async () => {
    const last = calls?.at(-1);
    if (!last) return;
    const page = await agentCalls(api, projectId, { before: last.at, limit: 50 });
    setCalls([...(calls ?? []), ...page]);
    setMore(page.length === 50);
  };

  return (
    <div className="agents-page">
      <header className="page-head">
        <h1 tabIndex={-1}>Agents on the Web</h1>
        <p className="page-sub">Let Claude and other agents you connect read this project and leave notes in its margin, from any device, with your computers off.</p>
      </header>
      {!app.online ? <p className="status-note">Offline: the switch and the agents’ calls need a connection.</p> : null}
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
      <section className="agents-card" aria-labelledby="agents-switch-title">
        <div className="agents-switch">
          <input
            id="agents-switch"
            type="checkbox"
            role="switch"
            checked={!!on}
            disabled={on === null || busy || !app.online || !listed}
            aria-describedby="agents-switch-help"
            data-agents={on === null || !listed ? 'unknown' : on ? 'on' : 'off'}
            onChange={(e) => (e.target.checked ? void switchOn() : setConfirmOff(true))}
          />
          <label htmlFor="agents-switch" id="agents-switch-title">
            Agents on the web
          </label>
        </div>
        <div className="help" id="agents-switch-help">
          <p>
            When this is on, Five Rooms’ server keeps a copy of this project’s key, so it can read the project while an agent you’ve connected
            works on it. The project stays encrypted at rest, as every project does. Projects with this off stay end-to-end encrypted: the server can’t
            read them and agents can’t see them.
          </p>
          <p>
            Turning it off stops agents reaching the project at once and deletes the server’s copy of the key. The server has seen that key, though:
            anything it stored before stays readable to it until the project gets a new key.
          </p>
        </div>
        {on ? (
          <p className="agents-connect">
            To connect Claude, add a custom connector in Claude’s settings with the URL <code>{mcpURL}</code>, then sign in here and choose what it may
            do.
          </p>
        ) : null}
      </section>

      <section className="agents-card" aria-labelledby="agents-connected-title">
        <h2 id="agents-connected-title">Connected</h2>
        {grants.length === 0 ? (
          <p className="empty-note">No agent can reach this project.</p>
        ) : (
          <ul className="agents-grants">
            {grants.map((g) => (
              <li key={g.id} data-grant-id={g.id}>
                <div>
                  <p className="agents-name">{g.clientName}</p>
                  <p className="agents-meta">
                    {permissions(g.gates)} · connected {DAY.format(g.createdAt)}
                  </p>
                </div>
                <button type="button" className="link-button danger" onClick={() => setDisconnecting(g)}>
                  Disconnect
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="agents-card" aria-labelledby="agents-calls-title">
        <h2 id="agents-calls-title">What agents did</h2>
        {calls === null ? <p className="empty-note">Loading…</p> : null}
        {calls?.length === 0 ? <p className="empty-note">No calls yet.</p> : null}
        {calls?.length ? (
          <ol className="agents-calls" aria-label="Agent calls, newest first" data-count={calls.length}>
            {calls.map((c) => (
              <CallRow key={c.id} call={c} projectKey={listed?.key ?? null} accountId={app.account.accountId} projectId={projectId} />
            ))}
          </ol>
        ) : null}
        {more ? (
          <button type="button" className="button secondary" onClick={() => void older()}>
            Show older calls
          </button>
        ) : null}
      </section>

      {confirmOff ? (
        <ConfirmDialog
          title="Turn off Agents on the web?"
          message={<p>Agents stop reaching this project at once, and it leaves every connection that had it. The server’s copy of the key is deleted.</p>}
          confirm="Turn Off"
          onCancel={() => setConfirmOff(false)}
          onConfirm={() => {
            setConfirmOff(false);
            void switchOff();
          }}
        />
      ) : null}
      {disconnecting ? (
        <ConfirmDialog
          title={`Disconnect ${disconnecting.clientName}?`}
          message={<p>It stops working on every project it could reach, at its next call. Connect it again from the app when you want it back.</p>}
          confirm="Disconnect"
          onCancel={() => setDisconnecting(null)}
          onConfirm={() => {
            const g = disconnecting;
            setDisconnecting(null);
            void revokeGrant(api, g.id)
              .then(() => announce(`${g.clientName} disconnected.`))
              .then(refresh)
              .catch((e) => setError(e instanceof Error ? e.message : String(e)));
          }}
        />
      ) : null}
    </div>
  );
}

function CallRow({ call, projectKey, accountId, projectId }: { call: AgentCall; projectKey: Uint8Array | null; accountId: string; projectId: string }) {
  const [details, setDetails] = useState<AgentCallDetails | null | 'none'>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const show = async () => {
    setOpen(!open);
    if (details !== null || !projectKey) return;
    try {
      setDetails((await openCall(projectKey, accountId, projectId, call)) ?? 'none');
    } catch {
      setProblem('These details don’t open with this project’s key.');
    }
  };
  const resultText = useMemo(() => {
    if (!details || details === 'none') return '';
    const r = details.result as { content?: { text?: string }[] } | undefined;
    return (r?.content ?? []).map((c) => c.text ?? '').join('\n');
  }, [details]);
  return (
    <li className="agents-call" data-call-id={call.id} data-tool={call.tool} data-outcome={call.outcome}>
      <div className="agents-call-line">
        <time dateTime={new Date(call.at).toISOString()}>{WHEN.format(call.at)}</time>
        <span className="agents-tool">{call.tool}</span>
        <span className="agents-outcome" data-outcome={call.outcome}>
          {OUTCOME[call.outcome]}
        </span>
        <span className="agents-client">{call.client}</span>
        {call.sealed ? (
          <button type="button" className="link-button" aria-expanded={open} onClick={() => void show()}>
            {open ? 'Hide details' : 'Details'}
          </button>
        ) : null}
      </div>
      {open ? (
        <div className="agents-details" data-call-details>
          {problem ? <p className="error">{problem}</p> : null}
          {details && details !== 'none' ? (
            <>
              <h3>Arguments</h3>
              <pre>{JSON.stringify(details.arguments, null, 2)}</pre>
              <h3>Result{details.truncated ? ' (cut short)' : ''}</h3>
              <pre>{resultText}</pre>
            </>
          ) : null}
          {details === 'none' ? <p className="empty-note">No details were kept.</p> : null}
        </div>
      ) : null}
    </li>
  );
}
